import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { deleteDocument, saveDocument, saveInternalDocument } from '../store/student/documents/actions';
import {
  DeleteDocumentPayload,
  SaveDocumentPayload,
  SaveInternalDocumentPayload,
} from '../store/student/documents/types';

export interface UseDocumentSaveResult {
  handleSaveDocument: (payload: Omit<SaveDocumentPayload, 'uni' | 'applicantId' | 'offerId'>) => void;
  handleDeleteDocument: (payload: Omit<DeleteDocumentPayload, 'uni' | 'applicantId' | 'offerId'>) => void;
  handleSaveInternalDocument: (payload: Omit<SaveInternalDocumentPayload, 'uni' | 'applicantId' | 'offerId'>) => void;
}

export const useDocumentSave = (): UseDocumentSaveResult => {
  const dispatch = useDispatch();
  const params = useParams();
  const { uni, userId: applicantId, offerId } = params;

  const handleSaveDocument = useCallback(
    (payload: Omit<SaveDocumentPayload, 'uni' | 'applicantId' | 'offerId'>) => {
      if (!uni || !applicantId) {
        console.error('Missing route parameters: uni or applicantId');
        return;
      }
      const fullPayload: SaveDocumentPayload & { uni: string; applicantId: string; offerId: string } = {
        ...payload,
        uni,
        applicantId,
        offerId,
      };

      dispatch(saveDocument(fullPayload));
    },
    [dispatch, uni, applicantId, offerId],
  );

  const handleDeleteDocument = useCallback(
    (payload: Omit<DeleteDocumentPayload, 'uni' | 'applicantId' | 'offerId'>) => {
      if (!uni || !applicantId) {
        console.error('Missing route parameters: uni or applicantId');
        return;
      }

      const fullPayload: DeleteDocumentPayload = {
        ...payload,
        uni,
        applicantId,
        offerId,
      };

      dispatch(deleteDocument(fullPayload));
    },
    [dispatch, uni, applicantId, offerId],
  );

  const handleSaveInternalDocument = useCallback(
    (payload: Omit<SaveInternalDocumentPayload, 'uni' | 'applicantId' | 'offerId'>) => {
      if (!uni || !applicantId || !offerId) {
        console.error('Missing route parameters: uni, applicantId or offerId');
        return;
      }

      const fullPayload: SaveInternalDocumentPayload = {
        ...payload,
        uni,
        applicantId,
        offerId,
      };
      dispatch(saveInternalDocument(fullPayload));
    },
    [dispatch, uni, applicantId, offerId],
  );

  return {
    handleSaveDocument,
    handleDeleteDocument,
    handleSaveInternalDocument,
  };
};
